import request from '../api/request'
import store from '../store'

/**
 * 浏览器直传 OSS（PostObject）。直传失败（如 CORS）时自动回退服务端 /files/upload。
 * @returns {Promise<{code:number,data:object}>} 与 /files/upload 相同结构
 */
export async function uploadFileDirect(file, onProgress) {
  if (!file) {
    throw new Error('请选择文件')
  }
  const policyRes = await request.get('/files/oss-policy', {
    params: {
      fileName: file.name || 'file',
      fileSize: file.size || 0,
      contentType: file.type || ''
    }
  })
  const policy = (policyRes && policyRes.data) || {}
  if (!policy.enabled || !policy.host || !policy.key) {
    return uploadViaServer(file, onProgress)
  }

  try {
    await postToOss(policy, file, onProgress)
    return request.post('/files/oss-complete', {
      key: policy.key,
      fileName: file.name || policy.fileName || 'file',
      fileSize: file.size || 0,
      contentType: file.type || policy.contentType || undefined
    })
  } catch (err) {
    if (shouldFallbackToServer(err)) {
      console.warn('[upload] OSS 直传不可用，改走服务端上传')
      return uploadViaServer(file, onProgress)
    }
    throw err
  }
}

function shouldFallbackToServer(err) {
  const msg = String((err && err.message) || err || '')
  return /CORS|cors|网络错误|Network Error|Failed to fetch|ERR_FAILED|preflight|Access-Control/i.test(msg)
}

function uploadViaServer(file, onProgress) {
  const fd = new FormData()
  fd.append('file', file)
  return request.post('/files/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
    onUploadProgress: (e) => {
      if (!onProgress || !e || !e.total) return
      onProgress(Math.round((e.loaded / e.total) * 100))
    }
  })
}

function postToOss(policy, file, onProgress) {
  return new Promise((resolve, reject) => {
    const fd = new FormData()
    fd.append('key', policy.key)
    fd.append('policy', policy.policy)
    fd.append('OSSAccessKeyId', policy.accessKeyId)
    fd.append('signature', policy.signature)
    fd.append('success_action_status', String(policy.successActionStatus || '200'))
    // 不传 Content-Type 字段，避免未写入 policy 导致 PostObject 校验失败
    fd.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', policy.host, true)
    xhr.timeout = 600000
    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return
      onProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
        return
      }
      if (xhr.status === 0) {
        reject(new Error('OSS 上传网络错误，请检查 Bucket CORS'))
        return
      }
      const text = (xhr.responseText || '').slice(0, 300)
      reject(new Error('OSS 上传失败(' + xhr.status + ')' + (text ? ': ' + text : '')))
    }
    xhr.onerror = () => reject(new Error('OSS 上传网络错误，请检查 Bucket CORS'))
    xhr.ontimeout = () => reject(new Error('OSS 上传超时'))
    xhr.send(fd)
  })
}

/** el-upload 的 http-request 适配 */
export function elUploadHttpRequest(options) {
  const file = options && options.file
  if (!file) {
    // 交给 Element UI：req.then(onSuccess, onError)，勿再手动调 onSuccess/onError，否则会回调两次
    return Promise.reject(new Error('请选择文件'))
  }
  return uploadFileDirect(file, (percent) => {
    if (options && typeof options.onProgress === 'function') {
      options.onProgress({ percent })
    }
  })
}

export function authUploadHeaders() {
  return { Authorization: 'Bearer ' + (store.state.token || '') }
}
